import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { inventoryItemIdFormat } from "@redotech/redo-inventory-id";
import { LABEL_SCAN_MARKETPLACES } from "@redotech/redo-order-common/provider";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const ReviewAddress_ = z.object({
  name: z.string().nullable(),
  street1: z.string().nullable(),
  street2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zipCode: z.string().nullable(),
  country: z.string().nullable(),
});
export interface ReviewAddress extends z.infer<typeof ReviewAddress_> {}
export const ReviewAddress: z.ZodType<ReviewAddress> = ReviewAddress_;

/** The operator-correctable fields used to create a return. */
const ShippingLabelReviewFields_ = z.object({
  carrier: createEnum(Carriers).nullable(),
  orderNumber: z.string().nullable(),
  rmaNumber: z.string().nullable(),
  trackingNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  customerPhone: z.string().nullable(),
  customerEmail: z.string().nullable(),
  merchantName: z.string().nullable(),
  marketplace: createEnum(LABEL_SCAN_MARKETPLACES).nullable(),
  weight: z.string().nullable(),
  senderAddress: ReviewAddress_.nullable(),
});
export interface ShippingLabelReviewFields extends z.infer<
  typeof ShippingLabelReviewFields_
> {}
export const ShippingLabelReviewFields: z.ZodType<ShippingLabelReviewFields> =
  ShippingLabelReviewFields_;

/** Shape of the stored `corrected` diff: only the operator-changed fields. */
export const ShippingLabelReviewFieldsPartial =
  ShippingLabelReviewFields_.partial();
export type ShippingLabelReviewFieldsPartial = z.infer<
  typeof ShippingLabelReviewFieldsPartial
>;

const ReviewReference_ = z.object({
  value: z.string(),
  context: z.string().nullable(),
});
export interface ReviewReference extends z.infer<typeof ReviewReference_> {}

/**
 * Set once the package has been confirmed into a return. The review UI locks
 * merchant selection to this: a package is attributed once, and re-confirming
 * under anyone else is refused, so the operator must not be offered the
 * choice a second time.
 */
const ExistingAttribution_ = z.object({
  returnId: z.string(),
  /** Mongo Team ObjectId hex of the merchant the package was attributed to */
  merchantTeamId: z.string(),
  /** Null when the scanning team can no longer access that merchant */
  merchantName: z.string().nullable(),
  /**
   * False when the return exists but the package is not bound to it — a
   * confirm that created the return and then failed before binding.
   * Re-confirming with this same merchant finishes the job.
   */
  linked: z.boolean(),
});
export interface ExistingAttribution extends z.infer<
  typeof ExistingAttribution_
> {}

export const getShippingLabelScanReview = {
  name: "getShippingLabelScanReview" as const,
  input: z.object({ inventoryItemId: inventoryItemIdFormat.schema }),
  output: z.object({
    attribution: ExistingAttribution_.nullable(),
    scan: z
      .object({
        scanId: z.string(),
        status: z.string(),
        photoUrl: z.string(),
        // Extraction values with any operator corrections already applied
        fields: ShippingLabelReviewFields_,
        // The untouched parse, so the operator can reset their corrections
        parsedFields: ShippingLabelReviewFields_,
        // Display-only context from the extraction
        destinationAddress: ReviewAddress_.nullable(),
        thirdPartyLogistics: z.string().nullable(),
        referenceNumbers: z.array(ReviewReference_),
      })
      .nullable(),
  }),
};
